import { isRelativeDateValue, resolveRelativeDateRange } from "./filter-relative";
export const operatorsByType = {
    text: ["eq", "neq", "contains", "startsWith", "in", "isNull", "notNull"],
    number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "isNull", "notNull"],
    boolean: ["eq", "neq", "isNull", "notNull"],
    date: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "isNull", "notNull"],
    datetime: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "isNull", "notNull"],
    enum: ["eq", "neq", "in", "isNull", "notNull"],
    reference: ["eq", "neq", "in", "isNull", "notNull"],
};
const isRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const isPrimitive = (v) => typeof v === "string" ||
    typeof v === "number" && Number.isFinite(v) || typeof v === "boolean";
const validDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) &&
    !Number.isNaN(Date.parse(v)) && new Date(v).toISOString().slice(0, 10) === v;
export const validateFilterExpression = (expression, fields) => {
    const problems = [];
    if (!isRecord(expression) || expression.schemaVersion !== 1 || !isRecord(expression.root)) {
        return ["Invalid expression version or root"];
    }
    const allowed = new Map(fields.map((field) => [field.id, field]));
    const ids = new Set();
    let count = 0;
    const visit = (node, depth) => {
        if (!isRecord(node)) {
            problems.push("Invalid filter node");
            return;
        }
        count++;
        if (count > 80) {
            problems.push("Filter has too many nodes");
            return;
        }
        if (depth > 6) {
            problems.push("Filter nesting exceeds limit");
            return;
        }
        if (typeof node.id !== "string" || node.id.length > 128 || !node.id || ids.has(node.id)) {
            problems.push("Filter IDs must be unique nonempty identifiers");
        }
        else
            ids.add(node.id);
        if (node.type === "group") {
            if (node.join !== "and" && node.join !== "or" || !Array.isArray(node.children)) {
                problems.push("Invalid logical group");
                return;
            }
            if (node.children.length > 80) {
                problems.push("Group too large");
                return;
            }
            if (depth > 0 && node.children.length === 0) {
                problems.push("Empty nested group");
                return;
            }
            for (const child of node.children)
                visit(child, depth + 1);
            return;
        }
        if (node.type !== "condition" || typeof node.field !== "string") {
            problems.push("Invalid condition");
            return;
        }
        const field = allowed.get(node.field);
        if (!field) {
            problems.push(`Unavailable filter property: ${node.field}`);
            return;
        }
        if (field.path && field.path.length > (field.maxRelationDepth ?? 3)) {
            problems.push(`Related property path not permitted: ${field.label}`);
            return;
        }
        const operators = field.operators ?? operatorsByType[field.type];
        if (!operators.includes(node.operator)) {
            problems.push(`Operator not permitted on ${field.label}`);
            return;
        }
        if (node.operator === "isNull" || node.operator === "notNull")
            return;
        const value = node.value;
        if (isRelativeDateValue(value)) {
            if ((field.type !== "date" && field.type !== "datetime") || node.operator !== "between") {
                problems.push(`Relative date requires a date range on ${field.label}`);
            }
            return;
        }
        if (value === undefined || value === null || value === "") {
            problems.push(`Enter a value for ${field.label}`);
            return;
        }
        if (node.operator === "in" || node.operator === "between") {
            if (!Array.isArray(value) || !value.length ||
                node.operator === "between" && value.length !== 2 ||
                value.some((item) => !isPrimitive(item))) {
                problems.push(`Invalid values for ${field.label}`);
                return;
            }
        }
        else if (!isPrimitive(value)) {
            problems.push(`Invalid value for ${field.label}`);
            return;
        }
        const scalars = Array.isArray(value) ? value : [value];
        if (field.type === "number" && scalars.some((item) => typeof item !== "number" || !Number.isFinite(item)))
            problems.push(`Numeric value required for ${field.label}`);
        if (field.type === "boolean" && scalars.some((item) => typeof item !== "boolean"))
            problems.push(`Boolean value required for ${field.label}`);
        if (field.type === "date" && scalars.some((item) => typeof item !== "string" || !validDate(item)))
            problems.push(`Valid date required for ${field.label}`);
        if (field.type === "datetime" && scalars.some((item) => typeof item !== "string" || !Number.isFinite(Date.parse(item))))
            problems.push(`Valid date-time required for ${field.label}`);
        if ((field.type === "enum" || field.type === "reference") && field.options &&
            scalars.some((item) => !field.options?.some((option) => option.id === item)))
            problems.push(`Unrecognized choice for ${field.label}`);
    };
    visit(expression.root, 0);
    return problems;
};
/** Structural URL decoding only; NOT field authorization. Revalidate with field schemas server-side. */
export const isFilterExpressionStructure = (input) => {
    if (!isRecord(input) || input.schemaVersion !== 1 || !isRecord(input.root))
        return false;
    let count = 0;
    const ids = new Set();
    const validNode = (node, depth) => {
        if (!isRecord(node) || ++count > 80 || depth > 6 ||
            typeof node.id !== "string" || !node.id || node.id.length > 128 ||
            ids.has(node.id))
            return false;
        ids.add(node.id);
        if (node.type === "group") {
            return (node.join === "and" || node.join === "or") &&
                Array.isArray(node.children) && node.children.length <= 80 &&
                node.children.every((child) => validNode(child, depth + 1));
        }
        if (node.type !== "condition" || typeof node.field !== "string" ||
            !node.field || node.field.length > 128 ||
            node.field === "__proto__" || node.field === "constructor" ||
            !Object.values(operatorsByType).some((operators) => operators.includes(node.operator)))
            return false;
        if (!Object.hasOwn(node, "value"))
            return true;
        const value = node.value;
        return value === null || isPrimitive(value) || isRelativeDateValue(value) ||
            Array.isArray(value) && value.length <= 50 && value.every(isPrimitive);
    };
    return validNode(input.root, 0);
};
export const serializeFilterExpression = (expression) => JSON.stringify(expression);
export const parseFilterExpression = (source, fields) => {
    if (source.length > 8000)
        return null;
    try {
        const decoded = JSON.parse(source);
        if (!isFilterExpressionStructure(decoded) || validateFilterExpression(decoded, fields).length)
            return null;
        return decoded;
    }
    catch {
        return null;
    }
};
export const withoutFilterValues = (expression) => {
    const strip = (node) => node.type === "condition" ?
        { type: "condition", id: node.id, field: node.field, operator: node.operator } :
        { ...node, children: node.children.map(strip) };
    return { ...expression, root: strip(expression.root) };
};
/** Reference-only evaluator for mock data and contract tests, never a substitute for server authorization. */
export const evaluateFilterExpression = (expression, row, fields) => {
    if (validateFilterExpression(expression, fields).length)
        return false;
    const allowed = new Map(fields.map((field) => [field.id, field]));
    const evaluate = (node) => {
        if (node.type === "group") {
            const items = node.children.map(evaluate);
            return !items.length || (node.join === "and" ? items.every(Boolean) : items.some(Boolean));
        }
        const field = allowed.get(node.field);
        if (!field)
            return false;
        const actual = row[node.field];
        if (node.operator === "isNull")
            return actual === null || actual === undefined;
        if (node.operator === "notNull")
            return actual !== null && actual !== undefined;
        const wanted = node.value;
        if (actual === null || actual === undefined || wanted === undefined || wanted === null)
            return false;
        const normalize = (v) => typeof v === "string" && (field.type === "text" || field.type === "enum" || field.type === "reference") ?
            v.toLowerCase() : typeof v === "number" || typeof v === "boolean" ? v : String(v);
        const left = normalize(actual);
        const right = isRelativeDateValue(wanted) ? [...resolveRelativeDateRange(wanted, new Date())] :
            Array.isArray(wanted) ? wanted.map(normalize) : normalize(wanted);
        switch (node.operator) {
            case "eq": return left === right;
            case "neq": return left !== right;
            case "contains": return typeof left === "string" && typeof right === "string" && left.includes(right);
            case "startsWith": return typeof left === "string" && typeof right === "string" && left.startsWith(right);
            case "gt": return typeof right !== "object" && left > right;
            case "gte": return typeof right !== "object" && left >= right;
            case "lt": return typeof right !== "object" && left < right;
            case "lte": return typeof right !== "object" && left <= right;
            case "between": return Array.isArray(right) && right.length === 2 &&
                left >= right[0] && left <= right[1];
            case "in": return Array.isArray(right) && right.includes(left);
        }
    };
    return evaluate(expression.root);
};
//# sourceMappingURL=filter-expression.js.map